import { injectLocal } from '@vueuse/core'
import { storeToRefs } from 'pinia'
import { computed, type Ref } from 'vue'

import { logError } from '@/logger'
import { isLocalDev } from '@/utils/environment/isLocalDev'
import { useReactiveFeatureFlag } from '@/utils/useFeatureFlags'

import { ACTIVE_BRAND_CONTEXT_KEY } from '../constants'
import { useCoreStore } from '../useCoreStore'

/**
 * Global application scope providing access to core brand and location context
 */
export interface ApplicationScope {
  activeBrandId: Ref<string>
  activeLocationId: Ref<string | undefined>
  hasLocations?: Ref<boolean>
}

/**
 * Returns the global application scope
 *
 * @returns The application scope with brand and location context
 */
export function useApplicationScope(): ApplicationScope {
  /**
   * If the ACTIVE_BRAND_CONTEXT_KEY is set, that means we are inside the
   * ActiveBrandProvider and that we definitely have data in the store. This allows us
   * to ignore the potential of an undefined value for activeBrandId
   */
  if (!injectLocal(ACTIVE_BRAND_CONTEXT_KEY)) {
    // tmp feature flag just to monitor evaluations and be able to turn off logging/erroring just in case
    const { enabled: logOrThrowErrors } = useReactiveFeatureFlag(
      'hermes.checkActiveBrandContext'
    )
    if (logOrThrowErrors.value) {
      const error =
        'should not use useApplicationScope outside of the active brand context'
      logError(error, { context: 'useApplicationScope', message: error })
      // throw error if in local dev for more visibility
      if (isLocalDev()) {
        throw new Error(error)
      }
    }
  }

  const {
    activeBrandId: piniaActiveBrandId,
    activeLocationId,
    activeBrandLocations,
  } = storeToRefs(useCoreStore())

  const activeBrandId = computed(
    () => (piniaActiveBrandId as Ref<string>).value // casting activeBrandId as defined due to the active brand context
  )

  const hasLocations = computed(() => activeBrandLocations.value.length > 0)

  return {
    activeBrandId,
    activeLocationId,
    hasLocations,
  }
}
